import React from "react";
import { Tooltip } from "@/components";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import { formatTimestampToAgo, getHostLogoStyleByHostId } from "@/utils";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import { PublishedIcon } from "@/components";
import { truncateText } from "@/utils/utilFunctions/truncateText";

type Props = {
  socials: any[];
  hosts: any[];
  hostOptions: any[];
  denmarkHosts: any[];
  isDraft?: boolean;
  showPopover: boolean;
  usePublishedIcon?: boolean;
  compactLogo?: boolean;
  onChipClick: (parent: DenmarkPublishInterface) => void;
  onPopoverChipClick?: (
    target: DenmarkPublishInterface,
    parent: DenmarkPublishInterface,
    anchor: DOMRect
  ) => void;
  onDeleteMultiTarget?: (
    target: DenmarkPublishInterface,
    parent: DenmarkPublishInterface
  ) => void;
};

const DenmarkMultiPublishChips: React.FC<Props> = ({
  socials,
  hosts,
  hostOptions,
  denmarkHosts,
  isDraft,
  showPopover,
  usePublishedIcon = false,
  compactLogo = false,
  onChipClick,
  onPopoverChipClick,
  onDeleteMultiTarget,
}) => {
  const allSavedDenmark = (socials || []).filter(
    (s) => s.job_type === "denmark"
  ) as DenmarkPublishInterface[];

  const standaloneIds = new Set(allSavedDenmark.map((s) => Number(s.section)));

  const multiTargetChips: Array<{
    target: DenmarkPublishInterface;
    parent: DenmarkPublishInterface;
  }> = [];
  const seenSections = new Set<number>();

  for (const parent of allSavedDenmark) {
    if ((parent as { _parentSectionId?: number })._parentSectionId != null) {
      continue;
    }
    if (
      !Array.isArray(parent.multi_publish_targets) ||
      parent.multi_publish_targets.length === 0
    ) {
      continue;
    }
    for (const target of parent.multi_publish_targets) {
      const sectionId = Number(target.section);
      if (!standaloneIds.has(sectionId) && !seenSections.has(sectionId)) {
        seenSections.add(sectionId);
        multiTargetChips.push({ target, parent });
      }
    }
  }

  return (
    <>
      {multiTargetChips.map(({ target, parent }) => (
        <div
          key={`multi-target-${target.section}`}
          className={`social-chip gap-1 ${usePublishedIcon ? "" : "p-1"}`}
          style={{
            cursor: showPopover ? "pointer" : "default",
            position: "relative",
            maxWidth: "46%",
          }}
          onClick={(e) => {
            if (!showPopover) return;
            if (onPopoverChipClick) {
              e.stopPropagation();
              const rect = (
                e.currentTarget as HTMLElement
              ).getBoundingClientRect();
              onPopoverChipClick(target, parent, rect);
            } else {
              onChipClick(parent);
            }
          }}
        >
          {usePublishedIcon ? (
            <PublishedIcon job_type="denmark" status={parent.status} />
          ) : (
            (() => {
              const logoStyle = getHostLogoStyleByHostId(
                target.host ?? parent.host,
                hosts,
                hostOptions,
                denmarkHosts
              );
              if (compactLogo) {
                return (
                  <span
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden"
                    style={{ width: "14px", height: "14px" }}
                  >
                    <div style={{ ...logoStyle, zoom: "0.44" }} />
                  </span>
                );
              }
              return <div style={logoStyle} />;
            })()
          )}
          <span
            className="text-truncate flex-grow-1"
            style={{ paddingRight: "10px" }}
          >
            {truncateText(
              target.channel_name || `Section ${target.section}`,
              13
            )}
          </span>
          {isDraft && onDeleteMultiTarget && (
            <i
              className="bi bi-x fs-4"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMultiTarget(target, parent);
              }}
            />
          )}
          {!isDraft && (
            <Tooltip
              content={
                parent?.schedule &&
                parent.schedule > Date.now() &&
                parent.status === 10
                  ? `Scheduled On: ${new Date(parent.schedule).toLocaleString()}`
                  : parent?.status === 10
                    ? `Published On: ${formatTimestampToAgo(
                        parent?.schedule || parent?.mt
                      )}`
                    : parent?.status === -2 || parent?.status === -1
                      ? (parent?.msg ?? "Your content has failed to publish.")
                      : parent?.status === 0
                        ? "Your content is in queue, uploading will begin once content is activated."
                        : "Your content is getting uploaded, currently in progress."
              }
            >
              {parent?.schedule &&
              parent.schedule > Date.now() &&
              parent.status === 10 ? (
                <i
                  className="bi bi-clock text-secondary me-1"
                  style={{ fontSize: "1rem" }}
                ></i>
              ) : (
                renderStatusIndicator(parent.status)
              )}
            </Tooltip>
          )}
        </div>
      ))}
    </>
  );
};

export default DenmarkMultiPublishChips;
